// 국가 드릴다운 (distributor-desk Design §4, FR-7). 라우트 /distributor/:countryId.
// 한 국가의 [총판 관계·계약·최근 발주·미해결 이슈·자료 공유·계약 개정]을 한 화면에
// 종합한다. 읽기 전용 뷰 — 생성/수정은 각 모듈 페이지에서 수행한다.
// 실적 수식은 flow 와 공유(saleCalc). GT 규칙: 확정 실적 행만 집계.
import { useMemo, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { krw, pct, saleCalc, usd } from "@/lib/margin/model";
import { saleToModel } from "@/flow/convert";
import { useDistributorData } from "./DistributorDeskLayout";
import {
  Badge,
  btnGhost,
  Card,
  cx,
  Disclaimer,
  EmptyState,
  IconChevron,
  IconFlag,
  PageHeader,
  SectionLabel,
  StatCard,
  tdCls,
  tdNum,
  thCls,
  type Tone,
} from "./ui";

// ── helpers ──────────────────────────────────────────────────────────────────
const maxDate = (xs: (string | null | undefined)[]): string | null => {
  const v = xs.filter((x): x is string => Boolean(x)).sort();
  return v.length ? v[v.length - 1]! : null;
};
const dayOnly = (s: string | null | undefined): string => (s ? s.slice(0, 10) : "—");
/** 문자열 날짜 내림차순 정렬(최신 우선). */
const descByDate = <T,>(arr: T[], key: (x: T) => string | null | undefined): T[] =>
  [...arr].sort((a, b) => (key(b) ?? "").localeCompare(key(a) ?? ""));

// ── label / tone maps ────────────────────────────────────────────────────────
const REL_STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: "활성", tone: "success" },
  dormant: { label: "휴면", tone: "warn" },
  terminated: { label: "종료", tone: "neutral" },
};
const EXCL: Record<string, { label: string; tone: Tone }> = {
  exclusive: { label: "독점", tone: "accent" },
  non_exclusive: { label: "비독점", tone: "neutral" },
  unset: { label: "미지정", tone: "neutral" },
};
const CONTRACT_STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: "활성", tone: "success" },
  done: { label: "완료", tone: "neutral" },
  cancelled: { label: "취소", tone: "danger" },
};
const ISSUE_SEVERITY: Record<string, { label: string; tone: Tone }> = {
  critical: { label: "심각", tone: "danger" },
  high: { label: "높음", tone: "warn" },
  medium: { label: "보통", tone: "neutral" },
  low: { label: "낮음", tone: "neutral" },
};
const ISSUE_STATUS: Record<string, { label: string; tone: Tone }> = {
  open: { label: "미착수", tone: "warn" },
  in_progress: { label: "진행중", tone: "accent" },
  resolved: { label: "해결", tone: "success" },
  closed: { label: "종료", tone: "neutral" },
};
const ISSUE_TYPE: Record<string, string> = {
  claim: "클레임",
  return: "반품",
  registration_delay: "인허가 지연",
  exclusivity_dispute: "독점권 분쟁",
  payment: "대금",
  logistics: "물류",
  other: "기타",
};
const DEAL_STATUS: Record<string, string> = {
  quote: "견적",
  order: "주문",
  ship_prep: "선적준비",
  doc_issued: "서류발행",
  customs_shipping: "통관·운송",
  done: "완료",
  cancelled: "취소",
};
const CHANNEL: Record<string, string> = {
  email: "이메일",
  portal: "포털",
  meeting: "미팅",
  courier: "특송",
  other: "기타",
};
const CHANGE_TYPE: Record<string, string> = {
  price: "가격",
  moq: "MOQ",
  exclusivity: "독점권",
  term: "기간",
  quota: "쿼터",
  license: "인허가",
  other: "기타",
};

export function CountryDetailPage(): JSX.Element {
  const d = useDistributorData();
  const { countryId = "" } = useParams<{ countryId: string }>();

  const country = useMemo(
    () => d.countries.find((c) => c.id === countryId) ?? null,
    [d.countries, countryId],
  );

  const partnerName = (id: string): string =>
    d.partners.find((p) => p.id === id)?.company_name ?? "(미상 총판)";
  const contractName = (id: string | null | undefined): string =>
    (id && d.contracts.find((c) => c.id === id)?.name) || "—";

  const view = useMemo(() => {
    const rels = d.relationships.filter((r) => r.country_id === countryId);
    const relIds = new Set(rels.map((r) => r.id));
    const partnerIds = new Set(rels.map((r) => r.partner_id));

    const contracts = d.contracts.filter(
      (c) => c.distributor_relationship_id != null && relIds.has(c.distributor_relationship_id),
    );
    const contractIds = new Set(contracts.map((c) => c.id));

    const contractRows = contracts.map((c) => {
      const sales = d.sales.filter((s) => s.contract_id === c.id);
      const soldQty = sales.reduce((sum, r) => sum + (r.qty || 0), 0);
      const revenueKrw = sales.reduce((sum, r) => sum + saleCalc(saleToModel(r)).revenueKrw, 0);
      const target = c.target_qty;
      const ratio = target && target > 0 ? soldQty / target : null;
      return {
        id: c.id,
        name: c.name,
        status: c.status,
        start: c.start_date,
        end: c.end_date,
        soldQty,
        target,
        ratio,
        revenueKrw,
      };
    });

    const dealsAll = d.deals.filter((x) => x.destination_country === countryId);
    const deals = descByDate(dealsAll, (x) => x.created_at).slice(0, 8);

    const provisionsAll = d.provisions.filter(
      (p) => p.country_id === countryId || partnerIds.has(p.partner_id),
    );
    const provisions = descByDate(provisionsAll, (p) => p.provided_at).slice(0, 8);

    const openIssues = d.issues.filter(
      (i) => i.country_id === countryId && (i.status === "open" || i.status === "in_progress"),
    );

    const amendmentsAll = d.amendments.filter((a) => contractIds.has(a.contract_id));
    const amendments = descByDate(amendmentsAll, (a) => a.amended_at).slice(0, 8);

    const kpi = {
      relCount: rels.length,
      activeContracts: contracts.filter((c) => c.status === "active").length,
      totalContracts: contracts.length,
      openIssues: openIssues.length,
      dealCount: dealsAll.length,
      lastProvidedAt: maxDate(provisionsAll.map((p) => p.provided_at)),
    };

    return { rels, contractRows, deals, provisions, openIssues, amendments, kpi };
  }, [d, countryId]);

  // ── guards ────────────────────────────────────────────────────────────────
  if (d.loading) {
    return (
      <Card className="px-4 py-16">
        <div className="text-center text-sm text-slate-400">불러오는 중…</div>
      </Card>
    );
  }

  if (!country) {
    return (
      <EmptyState
        icon={<IconFlag className="h-8 w-8" />}
        title={d.configured ? "국가를 찾을 수 없습니다" : "Supabase 미설정 — 국가 데이터가 없습니다"}
        action={
          <Link to="/distributor" className={btnGhost}>
            국가별 현황으로
          </Link>
        }
      >
        요청한 국가 코드{" "}
        <code className="rounded bg-slate-100 px-1 text-[12px] text-slate-500">
          {countryId || "(없음)"}
        </code>
        에 해당하는 프로파일이 없습니다.
      </EmptyState>
    );
  }

  const title = country.name_ko ?? country.name_en;
  const { rels, contractRows, deals, provisions, openIssues, amendments, kpi } = view;

  return (
    <div>
      <PageHeader
        eyebrow={country.id}
        title={title}
        subtitle="확정 실적 기준 집계 · 계약 개정 이력은 달성률과 별개 · 대외비 데이터는 CF Access 게이팅 이후 적재"
        actions={
          <Link to="/distributor" className={btnGhost}>
            국가별 현황
          </Link>
        }
      />

      {/* KPI 타일 */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="총판 관계" value={kpi.relCount} unit="곳" tone="accent" />
        <StatCard
          label="계약"
          value={kpi.activeContracts}
          unit="건 활성"
          hint={kpi.totalContracts > kpi.activeContracts ? `전체 ${kpi.totalContracts}건` : undefined}
        />
        <StatCard
          label="미해결 이슈"
          value={kpi.openIssues}
          unit="건"
          tone={kpi.openIssues > 0 ? "warn" : "neutral"}
        />
        <StatCard label="최근 자료공유" value={dayOnly(kpi.lastProvidedAt)} tone="info" />
      </div>

      <div className="space-y-5">
        {/* 총판 관계 */}
        <Section title="총판 관계" count={rels.length}>
          {rels.length === 0 ? (
            <Empty>등록된 총판 관계가 없습니다.</Empty>
          ) : (
            <TableWrap>
              <thead>
                <Head cols={["총판", "관계상태", "독점", "담당 영업", "관계 등급", "개시일"]} />
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rels.map((r) => (
                  <tr key={r.id} className="transition hover:bg-slate-50/60">
                    <td className={cx(tdCls, "font-medium text-slate-800")}>{partnerName(r.partner_id)}</td>
                    <td className={tdCls}>
                      <Badge tone={REL_STATUS[r.status]?.tone}>{REL_STATUS[r.status]?.label ?? r.status}</Badge>
                    </td>
                    <td className={tdCls}>
                      <Badge tone={EXCL[r.exclusivity]?.tone}>{EXCL[r.exclusivity]?.label ?? r.exclusivity}</Badge>
                    </td>
                    <td className={tdCls}>{r.owner || "—"}</td>
                    <td className={tdCls}>{r.relationship_tier || "—"}</td>
                    <td className={tdCls}>{dayOnly(r.relationship_start)}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>

        {/* 계약 */}
        <Section title="계약" count={contractRows.length} linkTo="/distributor/contracts" linkLabel="계약 변경 이력">
          {contractRows.length === 0 ? (
            <Empty>이 국가의 총판 관계에 연결된 계약이 없습니다.</Empty>
          ) : (
            <TableWrap>
              <thead>
                <tr className="border-b border-slate-200/70">
                  <th className={thCls}>계약명</th>
                  <th className={thCls}>상태</th>
                  <th className={thCls}>기간</th>
                  <th className={cx(thCls, "text-right")}>누적 판매</th>
                  <th className={cx(thCls, "text-right")}>목표</th>
                  <th className={cx(thCls, "text-right")}>달성</th>
                  <th className={cx(thCls, "text-right")}>매출 KRW</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contractRows.map((c) => (
                  <tr key={c.id} className="transition hover:bg-slate-50/60">
                    <td className={cx(tdCls, "font-medium text-slate-800")}>{c.name}</td>
                    <td className={tdCls}>
                      <Badge tone={CONTRACT_STATUS[c.status]?.tone}>
                        {CONTRACT_STATUS[c.status]?.label ?? c.status}
                      </Badge>
                    </td>
                    <td className={tdCls}>
                      {dayOnly(c.start)} ~ {dayOnly(c.end)}
                    </td>
                    <td className={tdNum}>{c.soldQty ? usd(c.soldQty) : "—"}</td>
                    <td className={tdNum}>{c.target != null ? usd(c.target) : "—"}</td>
                    <td className={tdNum}>{c.ratio != null ? pct(c.ratio) : "—"}</td>
                    <td className={cx(tdNum, "font-medium text-slate-800")}>{c.revenueKrw ? krw(c.revenueKrw) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>

        {/* 최근 발주 */}
        <Section title="최근 발주" count={kpi.dealCount}>
          {deals.length === 0 ? (
            <Empty>이 국가로 등록된 발주(딜)가 없습니다.</Empty>
          ) : (
            <TableWrap>
              <thead>
                <Head cols={["발주번호", "상태", "제목", "연결 계약", "등록일"]} />
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deals.map((x) => (
                  <tr key={x.id} className="transition hover:bg-slate-50/60">
                    <td className={tdCls}>
                      <Link to={`/deals/${x.id}`} className="font-medium text-indigo-600 hover:underline">
                        {x.deal_no}
                      </Link>
                    </td>
                    <td className={tdCls}>{DEAL_STATUS[x.status] ?? x.status}</td>
                    <td className={tdCls}>{x.title || "—"}</td>
                    <td className={tdCls}>{contractName(x.contract_id)}</td>
                    <td className={tdCls}>{dayOnly(x.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>

        {/* 미해결 이슈 */}
        <Section title="미해결 이슈" count={openIssues.length} linkTo="/distributor/issues" linkLabel="이슈 트래킹">
          {openIssues.length === 0 ? (
            <Empty>미해결(진행 중) 이슈가 없습니다.</Empty>
          ) : (
            <TableWrap>
              <thead>
                <Head cols={["제목", "유형", "심각도", "상태", "담당", "기한"]} />
              </thead>
              <tbody className="divide-y divide-slate-100">
                {openIssues.map((i) => (
                  <tr key={i.id} className="transition hover:bg-slate-50/60">
                    <td className={cx(tdCls, "font-medium text-slate-800")}>{i.title}</td>
                    <td className={tdCls}>{ISSUE_TYPE[i.issue_type] ?? i.issue_type}</td>
                    <td className={tdCls}>
                      <Badge tone={ISSUE_SEVERITY[i.severity]?.tone}>
                        {ISSUE_SEVERITY[i.severity]?.label ?? i.severity}
                      </Badge>
                    </td>
                    <td className={tdCls}>
                      <Badge tone={ISSUE_STATUS[i.status]?.tone}>
                        {ISSUE_STATUS[i.status]?.label ?? i.status}
                      </Badge>
                    </td>
                    <td className={tdCls}>{i.owner || "—"}</td>
                    <td className={tdCls}>{dayOnly(i.due_date)}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>

        {/* 최근 자료 공유 */}
        <Section title="최근 자료 공유" count={provisions.length} linkTo="/distributor/materials" linkLabel="파일 공유">
          {provisions.length === 0 ? (
            <Empty>이 국가/총판에 제공한 자료 이력이 없습니다.</Empty>
          ) : (
            <TableWrap>
              <thead>
                <Head cols={["제공일", "총판", "채널", "수신확인", "비고"]} />
              </thead>
              <tbody className="divide-y divide-slate-100">
                {provisions.map((p) => (
                  <tr key={p.id} className="transition hover:bg-slate-50/60">
                    <td className={tdCls}>{dayOnly(p.provided_at)}</td>
                    <td className={cx(tdCls, "font-medium text-slate-800")}>{partnerName(p.partner_id)}</td>
                    <td className={tdCls}>{p.channel ? CHANNEL[p.channel] ?? p.channel : "—"}</td>
                    <td className={tdCls}>
                      <Badge tone={p.acknowledged ? "success" : "warn"}>{p.acknowledged ? "확인" : "대기"}</Badge>
                    </td>
                    <td className={tdCls}>{p.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>

        {/* 최근 계약 개정 */}
        <Section title="최근 계약 개정" count={amendments.length} linkTo="/distributor/contracts" linkLabel="계약 변경 이력">
          {amendments.length === 0 ? (
            <Empty>이 국가 계약의 개정 이력이 없습니다.</Empty>
          ) : (
            <TableWrap>
              <thead>
                <Head cols={["개정일", "차수", "계약", "변경 유형", "사유"]} />
              </thead>
              <tbody className="divide-y divide-slate-100">
                {amendments.map((a) => (
                  <tr key={a.id} className="transition hover:bg-slate-50/60">
                    <td className={tdCls}>{dayOnly(a.amended_at)}</td>
                    <td className={tdCls}>#{a.amendment_no}</td>
                    <td className={cx(tdCls, "font-medium text-slate-800")}>{contractName(a.contract_id)}</td>
                    <td className={tdCls}>
                      {a.change_types.length ? (
                        <div className="flex flex-wrap gap-1">
                          {a.change_types.map((t) => (
                            <Badge key={t} tone="accent">
                              {CHANGE_TYPE[t] ?? t}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className={tdCls}>{a.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
      </div>

      <Disclaimer>
        본 도구는 사전 정의된 항목만 취합·표시합니다. 최종 판단·확인은 담당자 책임입니다.
      </Disclaimer>
    </div>
  );
}

// ── local presentational components ──────────────────────────────────────────
function Section({
  title,
  count,
  linkTo,
  linkLabel,
  children,
}: {
  title: string;
  count?: number;
  linkTo?: string;
  linkLabel?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200/70 px-4 py-3">
        <div className="flex items-baseline gap-2">
          <SectionLabel>{title}</SectionLabel>
          {typeof count === "number" && <span className="tnum text-[11px] text-slate-400">{count}</span>}
        </div>
        {linkTo && (
          <Link to={linkTo} className={btnGhost}>
            {linkLabel ?? "열기"}
            <IconChevron className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
      {children}
    </Card>
  );
}

function TableWrap({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">{children}</table>
    </div>
  );
}

function Head({ cols }: { cols: string[] }): JSX.Element {
  return (
    <tr className="border-b border-slate-200/70">
      {cols.map((h) => (
        <th key={h} className={thCls}>
          {h}
        </th>
      ))}
    </tr>
  );
}

function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <div className="px-4 py-8 text-center text-[13px] text-slate-400">{children}</div>;
}
